// AddFormulaElementModal.jsx
import { useMemo, useState } from "react";
import { FormModalControlled } from "../../../components/Modal";
import DropdownSelector from "../../../components/DropdownSelector";
import Input from "../../../components/Input";
import Label from "../../../components/Label";
import Button from "../../../components/Button";

const TOKEN_TYPES = [
  { id: "CONSTANT", label: "Konstanta" },
  { id: "PARAMETER", label: "Parametras" },
  { id: "OPERATOR", label: "Operatorius" },
  { id: "BRACKET", label: "Skliaustai" },
  { id: "NUMBER", label: "Skaičius" },
];

const OPERATOR_OPTIONS = [
  { id: "+", label: "+" },
  { id: "-", label: "-" },
  { id: "*", label: "*" },
  { id: "/", label: "/" },
];

const BRACKET_OPTIONS = [
  { id: "(", label: "(" },
  { id: ")", label: ")" },
];

// Simple id generator (no crypto dependency)
const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onAdd: (token) => void
 * - constants: constantsArray from useConstants ( [{id,label,value}, ...] )
 * - parameters: activeVariation?.parameters (typically [{width:100},{color:"..."}])
 * - disableChange: boolean
 */
const AddFormulaElementModal = ({
  isOpen,
  onClose,
  onAdd,
  constants,
  parameters,
  disableChange,
}) => {
  const [type, setType] = useState(TOKEN_TYPES[0]); // CONSTANT default
  const [selectedValue, setSelectedValue] = useState(null);
  const [numberValue, setNumberValue] = useState("");

  const constantOptions = useMemo(() => {
    if (!Array.isArray(constants)) return [];
    return constants.map((c) => ({ id: c.id, label: c.label || c.id }));
  }, [constants]);

  const parameterOptions = useMemo(() => {
    // parameters shape: [{ width: 100 }, { color: "..." }]
    if (!Array.isArray(parameters)) return [];
    return parameters
      .map((p) => Object.keys(p || {})[0])
      .filter(Boolean)
      .map((key) => {
        const id = String(key).toUpperCase();
        return { id, label: id };
      });
  }, [parameters]);

  const valueOptions = useMemo(() => {
    switch (type?.id) {
      case "CONSTANT":
        return constantOptions;
      case "PARAMETER":
        return parameterOptions;
      case "OPERATOR":
        return OPERATOR_OPTIONS;
      case "BRACKET":
        return BRACKET_OPTIONS;
      default:
        return [];
    }
  }, [type, constantOptions, parameterOptions]);

  const effectiveValue = selectedValue ?? valueOptions?.[0] ?? null;

  const canSubmit = useMemo(() => {
    if (disableChange) return false;

    if (type?.id === "NUMBER") {
      const v = String(numberValue).trim();
      if (v === "") return false;
      return !Number.isNaN(Number(v));
    }

    return effectiveValue?.id != null;
  }, [type, effectiveValue, numberValue, disableChange]);

  const resetLocalState = () => {
    setType(TOKEN_TYPES[0]);
    setSelectedValue(null);
    setNumberValue("");
  };

  const handleClose = () => {
    resetLocalState();
    onClose?.();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    const token =
      type.id === "NUMBER"
        ? { id: makeId(), type: "NUMBER", value: String(numberValue).trim() }
        : { id: makeId(), type: type.id, value: effectiveValue.id };

    onAdd?.(token);
    handleClose();
  };

  return (
    <FormModalControlled
      isOpen={isOpen}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      title="Pridėti elementą"
      submitLabel="Pridėti"
      cancelLabel="Atšaukti"
      disabled={!canSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap)",
          padding: "var(--gap)",
          minWidth: "min(560px, 90vw)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--gap)" }}>
          <div style={{ width: "var(--width-32)" }}>
            <Label>Tipas</Label>
          </div>
          <DropdownSelector
            options={TOKEN_TYPES}
            value={type}
            noEmpty
            onChange={(next) => {
              setType(next);
              // reset value fields when switching types
              setSelectedValue(null);
              setNumberValue("");
            }}
          />
        </div>

        {type?.id !== "NUMBER" ? (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--gap)" }}>
            <div style={{ width: "var(--width-32)" }}>
              <Label>Reikšmė</Label>
            </div>
            <DropdownSelector
              options={valueOptions}
              value={selectedValue}
              noEmpty
              onChange={(next) => setSelectedValue(next)}
              defaultValue={valueOptions?.[0]}
            />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--gap)" }}>
            <div style={{ width: "var(--width-32)" }}>
              <Label>Skaičius</Label>
            </div>
            <Input
              type="number"
              value={numberValue}
              onChange={(e) => setNumberValue(e.currentTarget.value)}
              placeholder="Pvz. 30"
            />
          </div>
        )}

        <div style={{ display: "flex", gap: "var(--gap-sm)", justifyContent: "flex-end" }}>
          {/* Optional: quick add another without closing */}
          <Button
            inline
            disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) return;
              const token =
                type.id === "NUMBER"
                  ? { id: makeId(), type: "NUMBER", value: String(numberValue).trim() }
                  : { id: makeId(), type: type.id, value: effectiveValue.id };
              onAdd?.(token);
              // keep modal open, reset only value
              setSelectedValue(null);
              setNumberValue("");
            }}
          >
            Pridėti dar
          </Button>
        </div>
      </div>
    </FormModalControlled>
  );
};

export default AddFormulaElementModal;
